import { useRef, useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Clock, Users, Sparkles, Building2, Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { PROGRAMS } from "@/data";
import { api, mediaUrl } from "@/lib/api";
import { useBooking } from "@/context/Booking";
import { fadeUp, staggerParent, viewportOnce, easeSoft } from "@/lib/animations";

const KIDS_FALLBACK = ["/programs/arttherapy", "/programs/creative-movement", "/programs/life-skills", "/programs/digital-detox"];

const ProgramPage = () => {
  const { slug } = useParams();
  const { openBooking } = useBooking();
  const [M, setM] = useState(null);
  // Tracks which slug M belongs to, so a route change never renders the previous page's data.
  const [loadedSlug, setLoadedSlug] = useState(null);
  const loaded = loadedSlug === slug;
  const [kidsHrefs, setKidsHrefs] = useState(KIDS_FALLBACK);

  useEffect(() => {
    api.get("/menu")
      .then((r) => {
        const grp = (Array.isArray(r.data) ? r.data : []).find((g) => /kid/i.test(g.label || ""));
        if (grp) setKidsHrefs((grp.items || []).map((i) => i.to).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  const [slideIdx, setSlideIdx] = useState(0);
  const slides = (M?.heroSlides || [])
    .map((s) => (typeof s === "string" ? { image: s, label: "" } : s))
    .filter((s) => s.image);

  useEffect(() => { setSlideIdx(0); }, [slug]);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length, slug]);

  // Wait for the CMS copy before rendering so the bundled images never flash first;
  // the bundled PROGRAMS entry is only a fallback when the API call fails.
  useEffect(() => {
    let active = true;
    api.get(`/pages/${slug}`)
      .then((r) => active && setM({ ...(PROGRAMS[slug] || {}), ...r.data }))
      .catch(() => active && setM(PROGRAMS[slug] || null))
      .finally(() => active && setLoadedSlug(slug));
    return () => { active = false; };
  }, [slug]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (!loaded) return <div className="min-h-screen bg-brand-cream" />;
  if (!M) return <Navigate to="/" replace />;

  const heroImg = mediaUrl(M.images?.hero);
  const spaceImg = mediaUrl(M.images?.space);
  const calmImg = mediaUrl(M.images?.calm);

  return (
    <div data-testid="program-page" key={slug}>
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-brand-cream pt-32 sm:pt-36 lg:pt-40">
        <div className="pointer-events-none absolute -left-40 top-10 h-[30rem] w-[30rem] rounded-full bg-brand-blue/10 blur-[120px]" />
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div className="flex flex-col justify-center pb-6 lg:pb-24">
            <motion.nav
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeSoft }}
              className="mb-7 flex flex-wrap items-center gap-2 font-body text-sm text-brand-ink/50"
              data-testid="breadcrumb"
            >
              <Link to="/" className="transition-colors duration-300 hover:text-brand-magenta">Home</Link>
              <span>/</span>
              <span>{M.category}</span>
              <span>/</span>
              <span className="text-brand-ink/80">{M.title}</span>
            </motion.nav>

            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeSoft, delay: 0.05 }}
              className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-brand-line bg-white/60 px-4 py-2 font-heading text-xs font-semibold uppercase tracking-[0.18em] text-brand-ink/70 backdrop-blur"
            >
              <Sparkles className="h-3.5 w-3.5 text-brand-magenta" />
              {M.category}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeSoft, delay: 0.1 }}
              className="font-heading text-[2.7rem] font-semibold leading-[0.95] tracking-tight text-brand-ink sm:text-6xl lg:text-[5rem]"
            >
              {M.titleLead && <>{M.titleLead}{" "}</>}
              <span className="brand-gradient-text">{M.accent}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeSoft, delay: 0.25 }}
              className="mt-7 max-w-xl font-body text-base leading-relaxed text-brand-ink/70 sm:text-lg"
            >
              {M.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeSoft, delay: 0.4 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <button
                type="button"
                onClick={() => openBooking(M.title)}
                data-testid="program-cta-book"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-magenta px-7 py-4 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]"
              >
                Book This Program
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <Link
                to="/"
                data-testid="program-back-home"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/25 px-7 py-4 font-body text-sm font-semibold text-brand-ink transition-colors duration-300 hover:border-brand-ink hover:bg-brand-ink hover:text-white"
              >
                Back to Home
              </Link>
            </motion.div>
          </div>

          <div className="relative flex items-end">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: easeSoft, delay: 0.35 }}
              style={{ y: imgY }}
              className="relative h-[420px] w-full overflow-hidden rounded-t-[160px] border border-brand-line bg-brand-ink sm:h-[520px] lg:h-[620px]"
            >
              {slides.length > 1 ? (
                <>
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={slideIdx}
                      src={mediaUrl(slides[slideIdx].image)}
                      alt={slides[slideIdx].label || M.title}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8, ease: easeSoft }}
                      className="absolute inset-0 h-full w-full object-cover object-center"
                      data-testid="program-slider-image"
                    />
                  </AnimatePresence>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
                  {slides[slideIdx].label && (
                    <span data-testid="program-slider-label" className="absolute bottom-16 left-5 rounded-full border border-white/25 bg-white/15 px-4 py-1.5 font-heading text-xs font-semibold uppercase tracking-[0.14em] text-white backdrop-blur">
                      {slides[slideIdx].label}
                    </span>
                  )}
                  <div className="absolute bottom-5 right-5 flex items-center gap-2" data-testid="program-hero-slider">
                    <button type="button" aria-label="Previous slide" onClick={() => setSlideIdx((slideIdx - 1 + slides.length) % slides.length)} data-testid="program-slider-prev" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur transition-colors duration-300 hover:bg-brand-magenta">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button type="button" aria-label="Next slide" onClick={() => setSlideIdx((slideIdx + 1) % slides.length)} data-testid="program-slider-next" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur transition-colors duration-300 hover:bg-brand-magenta">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="absolute bottom-7 left-5 flex gap-1.5">
                    {slides.map((_, i) => (
                      <button key={i} type="button" aria-label={`Go to slide ${i + 1}`} onClick={() => setSlideIdx(i)} data-testid={`program-slider-dot-${i}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === slideIdx ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <img src={heroImg} alt={M.title} className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
                </>
              )}
            </motion.div>
          </div>
        </div>

        {/* quick facts */}
        <div className="relative mt-6 border-y border-brand-line bg-white/50 backdrop-blur">
          <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-brand-line px-5 sm:grid-cols-4 sm:px-8">
            {M.facts.map((f) => (
              <div key={f.label} className="px-4 py-6" data-testid={`program-fact-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-heading text-xs font-bold uppercase tracking-[0.16em] text-brand-magenta">{f.label}</p>
                <p className="mt-2 font-heading text-lg font-semibold text-brand-ink">{f.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="bg-brand-cream py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <motion.p variants={fadeUp} className="mb-5 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-magenta">
              Overview
            </motion.p>
            <motion.h2 variants={fadeUp} className="max-w-xl font-heading text-4xl font-semibold leading-tight tracking-tight text-brand-ink sm:text-5xl">
              {M.overviewLead || "About the"}{" "}
              <span className="brand-gradient-text">{M.overviewAccent || "session"}</span>
            </motion.h2>
            {M.overview.map((p, i) => (
              <motion.p key={i} variants={fadeUp} className="mt-6 max-w-xl font-body text-base leading-relaxed text-brand-ink/70 sm:text-lg">
                {p}
              </motion.p>
            ))}
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="relative h-[380px] overflow-hidden rounded-[28px] border border-brand-line sm:h-[500px]"
          >
            <img src={spaceImg} alt={M.title} className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* PRACTICAL TECHNIQUES */}
      {M.techniques?.length > 0 && (
      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-2xl">
            <motion.p variants={fadeUp} className="mb-5 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-magenta">
              Practical Techniques
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl font-semibold leading-tight tracking-tight text-brand-ink sm:text-5xl">
              Techniques your teams <span className="brand-gradient-text">take away</span>
            </motion.h2>
          </motion.div>
          <motion.ul
            variants={staggerParent}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-14 grid grid-cols-1 overflow-hidden rounded-2xl border border-transparent brand-gradient sm:grid-cols-2 lg:grid-cols-3"
          >
            {M.techniques.map((t, i) => (
              <motion.li
                key={t}
                variants={fadeUp}
                data-testid={`program-technique-${i}`}
                className="group flex items-start gap-4 border-b border-r border-white/20 p-7 transition-colors duration-500 hover:bg-white/10"
              >
                <span className="font-heading text-sm font-bold tracking-widest text-white/60">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-body text-lg leading-snug text-white">{t}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>
      )}

      {/* QUOTE BAND */}
      {M.tagline && (
      <section className="grain relative overflow-hidden bg-brand-ink py-24 text-white sm:py-28">
        <div className="absolute inset-0">
          <img src={calmImg} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-brand-ink/75" />
        </div>
        <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-brand-magenta/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <p className="font-heading text-3xl font-semibold leading-snug sm:text-4xl lg:text-5xl">&ldquo;{M.tagline}&rdquo;</p>
        </div>
      </section>
      )}

      {!M.hideSections && (
      <>
      {/* BENEFITS */}
      <section className="bg-brand-cream py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-2xl">
            <motion.p variants={fadeUp} className="mb-5 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-magenta">
              The Impact
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl font-semibold leading-tight tracking-tight text-brand-ink sm:text-5xl">
              Human Transformation, <span className="brand-gradient-text">Relentless Growth.</span>
            </motion.h2>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              data-testid="program-helps"
              className="rounded-[26px] border border-brand-line bg-white p-9 lg:p-11"
            >
              <div className="mb-7 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full brand-gradient text-white">
                  <Heart className="h-5 w-5" />
                </span>
                <h3 className="font-heading text-2xl font-semibold text-brand-ink">How It Helps People</h3>
              </div>
              <p className="font-body text-base leading-relaxed text-brand-ink/75">{M.helps}</p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              data-testid="program-orgimpact"
              className="rounded-[26px] border border-brand-line bg-white p-9 lg:p-11"
            >
              <div className="mb-7 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full brand-gradient text-white">
                  <Building2 className="h-5 w-5" />
                </span>
                <h3 className="font-heading text-2xl font-semibold text-brand-ink">{kidsHrefs.includes(`/programs/${slug}`) ? "Impact on Participants" : "Organizational Impact"}</h3>
              </div>
              <ul className="space-y-4">
                {M.orgImpact.map((item) => (
                  <li key={item} className="flex items-start gap-3 font-body text-[15px] text-brand-ink/75">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
                    {item}
                  </li>
                ))}
              </ul>
              {M.orgNote && (
                <p className="mt-6 border-t border-brand-line pt-5 font-body text-sm leading-relaxed text-brand-ink/55">
                  {M.orgNote}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-2xl">
            <motion.p variants={fadeUp} className="mb-5 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-magenta">
              Session Formats
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl font-semibold leading-tight tracking-tight text-brand-ink sm:text-5xl">
              Choose the <span className="brand-gradient-text">right depth</span>
            </motion.h2>
          </motion.div>
          <motion.div
            variants={staggerParent}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {M.formats.map((f, i) => (
              <motion.div
                key={f.name}
                variants={fadeUp}
                data-testid={`program-format-${i}`}
                className="flex flex-col rounded-[22px] border border-brand-line bg-brand-cream p-7 transition-shadow duration-500 hover:shadow-xl hover:shadow-brand-ink/5"
              >
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1.5 font-body text-xs text-brand-ink/70">
                  <Clock className="h-3.5 w-3.5 text-brand-magenta" />
                  {f.time}
                </span>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-brand-ink">{f.name}</h3>
                <p className="mt-3 flex-1 font-body text-[15px] leading-relaxed text-brand-ink/65">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHO IT'S FOR + CTA */}
      <section className="bg-brand-cream py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_0.9fr] lg:items-center">
          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <motion.p variants={fadeUp} className="mb-5 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-magenta">
              Who It&apos;s For
            </motion.p>
            <motion.h2 variants={fadeUp} className="max-w-lg font-heading text-4xl font-semibold leading-tight tracking-tight text-brand-ink sm:text-5xl">
              Built for high-pressure, high-performance teams
            </motion.h2>
            <ul className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {M.whoFor.map((w) => (
                <motion.li key={w} variants={fadeUp} className="flex items-start gap-3 rounded-2xl border border-brand-line bg-white p-5 font-body text-[15px] text-brand-ink/75">
                  <Users className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
                  {w}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="grain relative overflow-hidden rounded-[28px] bg-brand-ink p-10 text-white lg:p-12"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-magenta/25 blur-[90px]" />
            <div className="relative">
              <h3 className="font-heading text-3xl font-semibold leading-tight sm:text-4xl">
                Bring {M.title} to your teams
              </h3>
              <p className="mt-4 font-body text-white/70">
                Tell us about your people and goals — we&apos;ll craft the right {M.title} experience for your organization.
              </p>
              <button
                type="button"
                onClick={() => openBooking(M.title)}
                data-testid="program-cta-book-bottom"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand-magenta px-7 py-4 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]"
              >
                Book This Program
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>
      </>
      )}
    </div>
  );
};

export default ProgramPage;
